import { useLocalStorage } from '@mantine/hooks';
import type { FeatureAccess } from '@repo/api-codegen';
import { useSuspenseGetWorkspaceFeatureAccess } from '@repo/api-codegen';
import { useCallback } from 'react';

/**
 * Hook to manage feature access flags for the workspace.
 * Uses suspense to wait for server data before rendering.
 */
export function useSuspenseFeatureAccess() {
	// eslint-disable-next-line no-local-storage/no-local-storage
	const [flagOverrides, setFlagOverrides] = useLocalStorage<
		Partial<FeatureAccess>
	>({
		key: 'feature-access-override',
		defaultValue: {},
	});

	// Fetch current features from server - this will suspend until data is ready
	const { data: features } = useSuspenseGetWorkspaceFeatureAccess({});

	const setOverride = useCallback(
		(flag: keyof FeatureAccess, value: boolean) => {
			setFlagOverrides((prev) => ({
				...prev,
				[flag]: value,
			}));
		},
		[setFlagOverrides]
	);

	const clearOverride = useCallback(
		(flag: keyof FeatureAccess) => {
			setFlagOverrides((prev) => ({
				...prev,
				[flag]: undefined,
			}));
		},
		[setFlagOverrides]
	);

	// Convert snake_case to camelCase for consistent API
	return {
		extensionAccess:
			flagOverrides.extension_access ?? features.extension_access,
		piiAccess: flagOverrides.pii_access ?? features.pii_access,
		exportAccess: flagOverrides.export_access ?? features.export_access,
		customInviteAccess:
			flagOverrides.custom_invite_access ?? features.custom_invite_access,
		monitoringAccess:
			flagOverrides.monitoring_access ?? features.monitoring_access,
		automationsAccess:
			flagOverrides.automations_access ?? features.automations_access,
		aiAccess: flagOverrides.ai_access ?? features.ai_access,
		apiAccess: flagOverrides.api_access ?? features.api_access,
		analyticsAccess:
			flagOverrides.analytics_access ?? features.analytics_access,
		secodaCoreAccess:
			flagOverrides.secoda_core_access ?? features.secoda_core_access,
		oldVersionHistoryAccess:
			flagOverrides.old_version_history_access ??
			features.old_version_history_access,
		samlAccess: flagOverrides.saml_access ?? features.saml_access,
		tunnelAccess: flagOverrides.tunnel_access ?? features.tunnel_access,
		groupAccess: flagOverrides.group_access ?? features.group_access,
		dqsAccess: flagOverrides.dqs_access ?? features.dqs_access,
		darAccess: flagOverrides.dar_access ?? features.dar_access,
		resourcePreviewAccess:
			flagOverrides.resource_preview_access ?? features.resource_preview_access,
		policiesAccess: flagOverrides.policies_access ?? features.policies_access,
		collectionsAccess:
			flagOverrides.collections_access ?? features.collections_access,
		lineageAccess: flagOverrides.lineage_access ?? features.lineage_access,
		auditLogAccess: flagOverrides.audit_log_access ?? features.audit_log_access,
		originalValues: features,
		overrideValues: flagOverrides,
		setOverride,
		clearOverride,
	};
}

export type FeatureAccessKeys = keyof Omit<
	ReturnType<typeof useSuspenseFeatureAccess>,
	| 'isLoading'
	| 'originalValues'
	| 'overrideValues'
	| 'setOverride'
	| 'clearOverride'
>;
